/**
 * Copy buttons on the README code blocks of the generated pages (/packages/*).
 *
 * Those blocks live inside README HTML that was baked at build time and
 * rendered via `dangerouslySetInnerHTML`, so the buttons cannot be part of
 * the React tree — {@link decorateCodeBlocks} attaches them client-side
 * after mount (and therefore never in the prerendered HTML). Pass it ONLY
 * that container: a `<pre>` React renders itself must get the React
 * `CodeCopyButton` instead, because moving React's node into a wrapper
 * breaks React's next update ("insertBefore … is not a child"). Each block's `<pre>` is wrapped in `.mol-code-wrap`
 * (position:relative) so the button can sit top-right OUTSIDE the pre's
 * overflow-x scroll, and stays fixed while the code scrolls horizontally.
 *
 * Idempotent by construction: a `<pre>` already inside a `.mol-code-wrap`
 * is skipped, so re-runs (data load, language change — labels are re-synced)
 * never duplicate buttons.
 */

import { writeText } from '@molecule/app-clipboard'

export interface CopyLabels {
  copy: string
  copied: string
}

/** How long the "Copied" feedback shows before reverting to "Copy". */
const FEEDBACK_MS = 1600

/**
 * Clipboard write through the clipboard core (`@molecule/app-clipboard`);
 * the app's web bond carries the hidden-textarea fallback. Resolves `false`
 * rather than throwing — the button simply keeps its "Copy" label when
 * copying is unavailable.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await writeText(text)
    return true
  } catch (error) {
    // No clipboard here (permission refused, insecure origin, no bond yet).
    console.debug('code copy: clipboard unavailable', error)
    return false
  }
}

/** The text a copy button copies: the block's code, minus the fence's trailing newline. */
function codeTextOf(pre: HTMLPreElement): string {
  return (pre.querySelector('code')?.textContent ?? pre.textContent ?? '').replace(/\n$/, '')
}

function createCopyButton(pre: HTMLPreElement, labels: CopyLabels): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'mol-copy-btn'
  button.dataset.molId = 'package-code-copy'
  button.textContent = labels.copy
  let timer: number | undefined
  button.addEventListener('click', () => {
    void copyToClipboard(codeTextOf(pre)).then((ok) => {
      if (!ok) return
      button.textContent = labels.copied
      button.classList.add('mol-copy-done')
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        button.textContent = labels.copy
        button.classList.remove('mol-copy-done')
      }, FEEDBACK_MS)
    })
  })
  return button
}

/**
 * Attach a copy button to every `<pre>` under `root` that does not have one
 * yet. Already-decorated blocks only get their labels re-synced (a language
 * change re-runs this with new labels).
 */
export function decorateCodeBlocks(root: HTMLElement, labels: CopyLabels): void {
  for (const pre of Array.from(root.querySelectorAll('pre'))) {
    const parent = pre.parentElement
    if (parent?.classList.contains('mol-code-wrap')) {
      const button = parent.querySelector<HTMLButtonElement>('.mol-copy-btn')
      if (button && !button.classList.contains('mol-copy-done')) {
        button.textContent = labels.copy
      }
      continue
    }
    const wrap = document.createElement('div')
    wrap.className = 'mol-code-wrap'
    pre.replaceWith(wrap)
    wrap.append(pre, createCopyButton(pre, labels))
  }
}
